/**
 * Biofit Fit Assistant — v2
 *
 * Changes from v1:
 *  - Step 1: direct waist input (primary) + height/weight estimate (fallback)
 *  - Algorithm: position-in-band boundary logic — preference only shifts at band edges
 *  - Formula: BMI-based waist estimation (2.0 × BMI + 40), more accurate than v1 linear
 */
(function () {
  'use strict';
  if (window.__biofitFitAssistantLoaded) return;
  window.__biofitFitAssistantLoaded = true;

  /* ─── state ─────────────────────────────────────────────────────── */
  var STATE = {
    input_mode: 'waist',   // 'waist' | 'estimate'
    waist: null,
    waist_unit: 'cm',
    height: null,
    height_unit: 'cm',
    weight: null,
    weight_unit: 'kg',
    fit: null,
    step: 1,
    matrix: null,
    size_option_name: 'Size'
  };

  /* ─── unit helpers ──────────────────────────────────────────────── */
  function toWaistCm(val, unit)  { return unit === 'in'  ? val * 2.54         : val; }
  function toHeightCm(val, unit) { return unit === 'in'  ? val * 2.54         : val; }
  function toWeightKg(val, unit) { return unit === 'lbs' ? val * 0.45359237   : val; }

  /* ─── waist estimation — BMI-based ─────────────────────────────── */
  // 2.0 × BMI + 40 calibrated for adult male, outperforms v1 linear formula
  // by ~5–7 cm for the 75–95 kg / 170–185 cm range typical of BIOFIT customers.
  function estimateWaistCm(height_cm, weight_kg) {
    var h_m = height_cm / 100;
    var bmi = weight_kg / (h_m * h_m);
    return 2.0 * bmi + 40;
  }

  /* ─── validation ranges ─────────────────────────────────────────── */
  var WAIST_RANGE  = { cm: [50, 150], in: [20, 60]   };
  var HEIGHT_RANGE = { cm: [100, 250], in: [40, 100]  };
  var WEIGHT_RANGE = { kg: [30, 250], lbs: [65, 550]  };

  /* ─── core recommendation: position-in-band ─────────────────────── */
  //
  //  Fit preference is only applied when the waist sits in the OUTER THIRD
  //  of a band.  Customers in the middle of their band get the same size
  //  regardless of preference — the fabric stretch handles minor variation.
  //
  //  Band layout (6 cm example):  ──[fitted zone]──[stable]──[loose zone]──
  //  bottom 33 %: fitted can drop one size
  //  middle 34 %: always returns the matched band (no shift)
  //  top 33 %:    loose can rise one size
  //
  function recommendSize(waist_cm, matrix, fit_pref) {
    if (!matrix || !matrix.length) return null;

    /* 1. find the band that contains the waist (exact) */
    var bandIdx = -1;
    for (var i = 0; i < matrix.length; i++) {
      var r = matrix[i].waist_cm;
      if (!r) continue;
      if (waist_cm >= r[0] && waist_cm <= r[1]) { bandIdx = i; break; }
    }

    /* 2. if outside all bands, clamp to nearest */
    if (bandIdx === -1) {
      var minDist = Infinity;
      for (var i = 0; i < matrix.length; i++) {
        var r = matrix[i].waist_cm;
        if (!r) continue;
        var dist = waist_cm < r[0] ? r[0] - waist_cm : waist_cm - r[1];
        if (dist < minDist) { minDist = dist; bandIdx = i; }
      }
    }
    if (bandIdx === -1) return null;

    var band   = matrix[bandIdx];
    var bMin   = band.waist_cm[0];
    var bMax   = band.waist_cm[1];
    var bWidth = bMax - bMin;
    /* relative position: 0.0 = bottom of band, 1.0 = top */
    var relPos = bWidth > 0 ? Math.max(0, Math.min(1, (waist_cm - bMin) / bWidth)) : 0.5;

    /* 3. apply boundary preference */
    var targetIdx = bandIdx;
    if (fit_pref === 'fitted' && relPos < 0.33 && bandIdx > 0) {
      targetIdx = bandIdx - 1;
    } else if (fit_pref === 'loose' && relPos > 0.67 && bandIdx < matrix.length - 1) {
      targetIdx = bandIdx + 1;
    }

    /* 4. confidence: 99 % at band centre → 78 % at band edge */
    var distFromCentre = Math.abs(relPos - 0.5) * 2;
    var confidence = Math.max(60, Math.round(99 - distFromCentre * 21));
    var isOutside = waist_cm < matrix[0].waist_cm[0] || waist_cm > matrix[matrix.length - 1].waist_cm[1];
    if (isOutside) confidence = 65;

    /* 5. secondary: show adjacent size only when near a boundary (outer 25 %) */
    var second = null;
    if (relPos > 0.75 && targetIdx < matrix.length - 1) {
      second = { size: matrix[targetIdx + 1].size, confidence: Math.round(confidence * 0.72) };
    } else if (relPos < 0.25 && targetIdx > 0) {
      second = { size: matrix[targetIdx - 1].size, confidence: Math.round(confidence * 0.72) };
    }

    return {
      recommended_size: matrix[targetIdx].size,
      recommended_idx:  targetIdx,
      confidence:       confidence,
      second:           second
    };
  }

  /* ─── variant pre-select ────────────────────────────────────────── */
  function preSelectVariant(sizeName, optionName) {
    if (!sizeName) return false;
    var nameLower = (optionName || 'size').toLowerCase();

    var fieldsets = document.querySelectorAll('variant-radios fieldset[data-option-slug], variant-radios fieldset');
    for (var i = 0; i < fieldsets.length; i++) {
      var fs = fieldsets[i];
      var slug       = (fs.getAttribute('data-option-slug') || '').toLowerCase();
      var legend     = fs.querySelector('legend');
      var legendText = legend ? legend.textContent.trim().toLowerCase() : '';
      if (slug.indexOf(nameLower) === -1 && legendText.indexOf(nameLower) === -1) continue;
      var inputs = fs.querySelectorAll('input[type="radio"]');
      for (var j = 0; j < inputs.length; j++) {
        var inp = inputs[j];
        var val = inp.getAttribute('value') || inp.getAttribute('data-option-value');
        if (val && val.toLowerCase() === sizeName.toLowerCase() && !inp.disabled) {
          inp.click();
          return true;
        }
      }
    }

    var selects = document.querySelectorAll('variant-selects select');
    for (var k = 0; k < selects.length; k++) {
      var sel     = selects[k];
      var selName = (sel.getAttribute('name') || '').toLowerCase();
      if (selName.indexOf(nameLower) === -1) continue;
      for (var m = 0; m < sel.options.length; m++) {
        if (sel.options[m].value.toLowerCase() === sizeName.toLowerCase()) {
          sel.value = sel.options[m].value;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }
      }
    }
    return false;
  }

  /* ─── modal markup ──────────────────────────────────────────────── */
  function buildModal() {
    var html = ''
      + '<div class="biofit-fa-overlay" data-biofit-fa-overlay></div>'
      + '<div class="biofit-fa-modal" role="dialog" aria-modal="true" aria-label="Fit Assistant" data-biofit-fa-modal>'
      +   '<div class="biofit-fa-handle" aria-hidden="true"></div>'
      +   '<div class="biofit-fa-header">'
      +     '<h2 class="biofit-fa-header__title">Fit Assistant</h2>'
      +     '<button type="button" class="biofit-fa-close" aria-label="Close" data-biofit-fa-close>'
      +       '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
      +         '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>'
      +       '</svg>'
      +     '</button>'
      +   '</div>'
      +   '<div class="biofit-fa-progress">'
      +     '<span class="biofit-fa-progress__dot is-active" data-step-dot="1"></span>'
      +     '<span class="biofit-fa-progress__dot" data-step-dot="2"></span>'
      +     '<span class="biofit-fa-progress__dot" data-step-dot="3"></span>'
      +   '</div>'
      +   '<div class="biofit-fa-body">'

      /* ── Step 1 ── */
      +     '<div class="biofit-fa-step is-active" data-step="1">'
      +       '<h3 class="biofit-fa-step__title">What\'s your waist size?</h3>'
      +       '<p class="biofit-fa-step__sub">Measure around where your waistband sits — snug but not squeezing.</p>'

      /* waist mode (primary) */
      +       '<div data-mode-panel="waist">'
      +         '<div class="biofit-fa-field">'
      +           '<div class="biofit-fa-field__row">'
      +             '<span class="biofit-fa-field__label">Waist</span>'
      +             '<div class="biofit-fa-toggle" data-toggle="waist">'
      +               '<button type="button" class="biofit-fa-toggle__btn is-active" data-unit="cm">CM</button>'
      +               '<button type="button" class="biofit-fa-toggle__btn" data-unit="in">IN</button>'
      +             '</div>'
      +           '</div>'
      +           '<div class="biofit-fa-input-wrap">'
      +             '<input type="number" inputmode="decimal" class="biofit-fa-input" data-input="waist" placeholder="">'
      +             '<span class="biofit-fa-input-suffix" data-suffix="waist">CM</span>'
      +           '</div>'
      +         '</div>'
      +         '<button type="button" class="biofit-fa-mode-link" data-switch-mode="estimate">'
      +           'No tape measure? Estimate from height &amp; weight'
      +         '</button>'
      +       '</div>'

      /* estimate mode (fallback) */
      +       '<div data-mode-panel="estimate" style="display:none;">'
      +         '<div class="biofit-fa-field">'
      +           '<div class="biofit-fa-field__row">'
      +             '<span class="biofit-fa-field__label">Height</span>'
      +             '<div class="biofit-fa-toggle" data-toggle="height">'
      +               '<button type="button" class="biofit-fa-toggle__btn is-active" data-unit="cm">CM</button>'
      +               '<button type="button" class="biofit-fa-toggle__btn" data-unit="in">IN</button>'
      +             '</div>'
      +           '</div>'
      +           '<div class="biofit-fa-input-wrap">'
      +             '<input type="number" inputmode="decimal" class="biofit-fa-input" data-input="height" placeholder="">'
      +             '<span class="biofit-fa-input-suffix" data-suffix="height">CM</span>'
      +           '</div>'
      +         '</div>'
      +         '<div class="biofit-fa-field">'
      +           '<div class="biofit-fa-field__row">'
      +             '<span class="biofit-fa-field__label">Weight</span>'
      +             '<div class="biofit-fa-toggle" data-toggle="weight">'
      +               '<button type="button" class="biofit-fa-toggle__btn is-active" data-unit="kg">KG</button>'
      +               '<button type="button" class="biofit-fa-toggle__btn" data-unit="lbs">LBS</button>'
      +             '</div>'
      +           '</div>'
      +           '<div class="biofit-fa-input-wrap">'
      +             '<input type="number" inputmode="decimal" class="biofit-fa-input" data-input="weight" placeholder="">'
      +             '<span class="biofit-fa-input-suffix" data-suffix="weight">KG</span>'
      +           '</div>'
      +         '</div>'
      +         '<button type="button" class="biofit-fa-mode-link" data-switch-mode="waist">'
      +           '← I have a tape measure'
      +         '</button>'
      +       '</div>'
      +     '</div>'

      /* ── Step 2 ── */
      +     '<div class="biofit-fa-step" data-step="2">'
      +       '<h3 class="biofit-fa-step__title">How do you like it to feel?</h3>'
      +       '<p class="biofit-fa-step__sub">Adjusts your recommendation if you\'re near a size boundary.</p>'
      +       '<div class="biofit-fa-options">'
      +         '<button type="button" class="biofit-fa-option" data-fit="fitted">'
      +           '<span class="biofit-fa-option__title">Fitted</span>'
      +           '<span class="biofit-fa-option__sub">Firm, close to body</span>'
      +         '</button>'
      +         '<button type="button" class="biofit-fa-option" data-fit="just_right">'
      +           '<span class="biofit-fa-option__title">Just right</span>'
      +           '<span class="biofit-fa-option__sub">True to size</span>'
      +         '</button>'
      +         '<button type="button" class="biofit-fa-option" data-fit="loose">'
      +           '<span class="biofit-fa-option__title">Relaxed</span>'
      +           '<span class="biofit-fa-option__sub">More room to move</span>'
      +         '</button>'
      +       '</div>'
      +     '</div>'

      /* ── Step 3 ── */
      +     '<div class="biofit-fa-step" data-step="3">'
      +       '<div class="biofit-fa-result">'
      +         '<p class="biofit-fa-result__intro">We recommend this size for you.</p>'
      +         '<div class="biofit-fa-result__badge">'
      +           '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
      +             '<path d="M12 2v4"/><path d="M3 22h18"/><path d="M3 22 12 8l9 14"/>'
      +             '<path d="M12 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"/>'
      +           '</svg>'
      +           '<span data-result="size">—</span>'
      +         '</div>'
      +         '<div class="biofit-fa-bar" data-result-bar="primary">'
      +           '<div class="biofit-fa-bar__row">'
      +             '<span class="biofit-fa-bar__size" data-bar-size>—</span>'
      +             '<span class="biofit-fa-bar__pct" data-bar-pct>0%</span>'
      +           '</div>'
      +           '<div class="biofit-fa-bar__track"><div class="biofit-fa-bar__fill" data-bar-fill style="width:0%"></div></div>'
      +         '</div>'
      +         '<div class="biofit-fa-bar biofit-fa-bar--secondary" data-result-bar="secondary" style="display:none;">'
      +           '<div class="biofit-fa-bar__row">'
      +             '<span class="biofit-fa-bar__size" data-bar-size>—</span>'
      +             '<span class="biofit-fa-bar__pct" data-bar-pct>0%</span>'
      +           '</div>'
      +           '<div class="biofit-fa-bar__track"><div class="biofit-fa-bar__fill" data-bar-fill style="width:0%"></div></div>'
      +         '</div>'
      +         '<p class="biofit-fa-result__note">Based on your <strong>measurements</strong> and fit preference.</p>'
      +       '</div>'
      +     '</div>'

      +   '</div>'
      +   '<div class="biofit-fa-footer">'
      +     '<button type="button" class="biofit-fa-btn biofit-fa-btn--primary" data-biofit-fa-action="next" disabled>Continue</button>'
      +     '<button type="button" class="biofit-fa-btn biofit-fa-btn--ghost" data-biofit-fa-action="back" style="display:none;">Back</button>'
      +     '<button type="button" class="biofit-fa-btn biofit-fa-btn--ghost" data-biofit-fa-action="restart" style="display:none;">Start again</button>'
      +   '</div>'
      + '</div>';

    var wrap = document.createElement('div');
    wrap.setAttribute('data-biofit-fa-root', '');
    wrap.innerHTML = html;
    document.body.appendChild(wrap);
    return wrap;
  }

  /* ─── modal controller ──────────────────────────────────────────── */
  var rootEl = null;
  var overlayEl, modalEl, closeBtn;

  function ensureModal() {
    if (rootEl) return rootEl;
    rootEl   = buildModal();
    overlayEl = rootEl.querySelector('[data-biofit-fa-overlay]');
    modalEl   = rootEl.querySelector('[data-biofit-fa-modal]');
    closeBtn  = rootEl.querySelector('[data-biofit-fa-close]');

    overlayEl.addEventListener('click', closeModal);
    closeBtn.addEventListener('click', closeModal);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlayEl.classList.contains('is-open')) closeModal();
    });

    bindStep1Events();
    bindStep2Events();
    bindFooterEvents();
    return rootEl;
  }

  /* ─── error display ─────────────────────────────────────────────── */
  function showInputError(msg) {
    var existing = rootEl.querySelector('.biofit-fa-input-error');
    if (existing) existing.remove();
    if (!msg) return;
    var el = document.createElement('p');
    el.className = 'biofit-fa-input-error';
    el.textContent = msg;
    var panel = rootEl.querySelector('[data-mode-panel="' + STATE.input_mode + '"]');
    if (panel) panel.appendChild(el);
  }

  /* ─── step 1 events ─────────────────────────────────────────────── */
  function bindStep1Events() {
    /* mode switchers */
    rootEl.querySelectorAll('[data-switch-mode]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        switchInputMode(btn.getAttribute('data-switch-mode'));
      });
    });

    /* unit toggles */
    rootEl.querySelectorAll('.biofit-fa-toggle').forEach(function (toggle) {
      var which = toggle.getAttribute('data-toggle');
      toggle.querySelectorAll('.biofit-fa-toggle__btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var unit = btn.getAttribute('data-unit');
          STATE[which + '_unit'] = unit;
          toggle.querySelectorAll('.biofit-fa-toggle__btn').forEach(function (b) { b.classList.remove('is-active'); });
          btn.classList.add('is-active');
          var suffix = rootEl.querySelector('[data-suffix="' + which + '"]');
          if (suffix) suffix.textContent = unit.toUpperCase();
          showInputError(null);
          updateContinueState();
        });
      });
    });

    /* number inputs */
    rootEl.querySelectorAll('.biofit-fa-input').forEach(function (input) {
      input.addEventListener('input', updateContinueState);
    });
  }

  function switchInputMode(mode) {
    STATE.input_mode = mode;
    STATE.waist  = null;
    STATE.height = null;
    STATE.weight = null;
    rootEl.querySelectorAll('.biofit-fa-input').forEach(function (i) { i.value = ''; });
    showInputError(null);
    rootEl.querySelectorAll('[data-mode-panel]').forEach(function (panel) {
      panel.style.display = panel.getAttribute('data-mode-panel') === mode ? '' : 'none';
    });
    updateContinueState();
  }

  /* ─── step 2 events ─────────────────────────────────────────────── */
  function bindStep2Events() {
    rootEl.querySelectorAll('.biofit-fa-option').forEach(function (opt) {
      opt.addEventListener('click', function () {
        rootEl.querySelectorAll('.biofit-fa-option').forEach(function (o) { o.classList.remove('is-active'); });
        opt.classList.add('is-active');
        STATE.fit = opt.getAttribute('data-fit');
        updateContinueState();
      });
    });
  }

  /* ─── footer events ─────────────────────────────────────────────── */
  function bindFooterEvents() {
    rootEl.querySelector('[data-biofit-fa-action="next"]').addEventListener('click', goNext);
    rootEl.querySelector('[data-biofit-fa-action="back"]').addEventListener('click', goBack);
    rootEl.querySelector('[data-biofit-fa-action="restart"]').addEventListener('click', restart);
  }

  /* ─── continue state ────────────────────────────────────────────── */
  function updateContinueState() {
    var nextBtn = rootEl.querySelector('[data-biofit-fa-action="next"]');
    var ok = false;

    if (STATE.step === 1) {
      showInputError(null);

      if (STATE.input_mode === 'waist') {
        var w = parseFloat(rootEl.querySelector('[data-input="waist"]').value);
        var wRange = WAIST_RANGE[STATE.waist_unit];
        if (!isNaN(w) && w > 0 && (w < wRange[0] || w > wRange[1])) {
          showInputError('Please check your waist — valid range is ' + wRange[0] + '–' + wRange[1] + ' ' + STATE.waist_unit + '.');
        } else {
          STATE.waist = (!isNaN(w) && w >= wRange[0] && w <= wRange[1]) ? w : null;
          ok = STATE.waist !== null;
        }

      } else {
        var h     = parseFloat(rootEl.querySelector('[data-input="height"]').value);
        var wt    = parseFloat(rootEl.querySelector('[data-input="weight"]').value);
        var hRange  = HEIGHT_RANGE[STATE.height_unit];
        var wtRange = WEIGHT_RANGE[STATE.weight_unit];
        var hValid  = !isNaN(h)  && h  >= hRange[0]  && h  <= hRange[1];
        var wtValid = !isNaN(wt) && wt >= wtRange[0] && wt <= wtRange[1];

        if (!isNaN(h) && h > 0 && !hValid) {
          showInputError('Please check your height — valid range is ' + hRange[0] + '–' + hRange[1] + ' ' + STATE.height_unit + '.');
        } else if (!isNaN(wt) && wt > 0 && !wtValid) {
          showInputError('Please check your weight — valid range is ' + wtRange[0] + '–' + wtRange[1] + ' ' + STATE.weight_unit + '.');
        }
        STATE.height = hValid  ? h  : null;
        STATE.weight = wtValid ? wt : null;
        ok = hValid && wtValid;
      }

    } else if (STATE.step === 2) {
      ok = !!STATE.fit;
    } else {
      ok = true;
    }

    nextBtn.disabled = !ok;
  }

  /* ─── step transitions ──────────────────────────────────────────── */
  function setStep(step) {
    STATE.step = step;
    rootEl.querySelectorAll('.biofit-fa-step').forEach(function (el) {
      el.classList.toggle('is-active', el.getAttribute('data-step') === String(step));
    });
    rootEl.querySelectorAll('[data-step-dot]').forEach(function (dot) {
      dot.classList.toggle('is-active', parseInt(dot.getAttribute('data-step-dot'), 10) <= step);
    });

    var nextBtn    = rootEl.querySelector('[data-biofit-fa-action="next"]');
    var backBtn    = rootEl.querySelector('[data-biofit-fa-action="back"]');
    var restartBtn = rootEl.querySelector('[data-biofit-fa-action="restart"]');

    nextBtn.style.display    = '';
    backBtn.style.display    = step === 2 ? '' : 'none';
    restartBtn.style.display = step === 3 ? '' : 'none';

    if (step === 1) { nextBtn.textContent = 'Continue'; }
    else if (step === 2) { nextBtn.textContent = 'See my size'; }
    else { nextBtn.textContent = 'Continue shopping'; }

    updateContinueState();
  }

  function goNext() {
    if (STATE.step === 1)      { setStep(2); }
    else if (STATE.step === 2) { computeAndShowResult(); setStep(3); }
    else                       { closeModal(); }
  }

  function goBack() { if (STATE.step > 1) setStep(STATE.step - 1); }

  function restart() {
    STATE.waist  = null;
    STATE.height = null;
    STATE.weight = null;
    STATE.fit    = null;
    STATE.input_mode = 'waist';
    rootEl.querySelectorAll('.biofit-fa-input').forEach(function (i) { i.value = ''; });
    rootEl.querySelectorAll('.biofit-fa-option').forEach(function (o) { o.classList.remove('is-active'); });
    rootEl.querySelectorAll('[data-mode-panel]').forEach(function (panel) {
      panel.style.display = panel.getAttribute('data-mode-panel') === 'waist' ? '' : 'none';
    });
    showInputError(null);
    setStep(1);
  }

  /* ─── compute result ────────────────────────────────────────────── */
  function computeAndShowResult() {
    var waist_cm;
    if (STATE.input_mode === 'waist') {
      waist_cm = toWaistCm(STATE.waist, STATE.waist_unit);
    } else {
      var h_cm  = toHeightCm(STATE.height, STATE.height_unit);
      var w_kg  = toWeightKg(STATE.weight, STATE.weight_unit);
      waist_cm  = estimateWaistCm(h_cm, w_kg);
    }

    var rec = recommendSize(waist_cm, STATE.matrix, STATE.fit);
    if (!rec) return;

    rootEl.querySelector('[data-result="size"]').textContent = rec.recommended_size;

    var primaryBar = rootEl.querySelector('[data-result-bar="primary"]');
    primaryBar.querySelector('[data-bar-size]').textContent = rec.recommended_size;
    primaryBar.querySelector('[data-bar-pct]').textContent  = rec.confidence + '%';
    setTimeout(function () {
      primaryBar.querySelector('[data-bar-fill]').style.width = rec.confidence + '%';
    }, 50);

    var secondaryBar = rootEl.querySelector('[data-result-bar="secondary"]');
    if (rec.second && rec.second.confidence >= 40) {
      secondaryBar.style.display = '';
      secondaryBar.querySelector('[data-bar-size]').textContent = rec.second.size;
      secondaryBar.querySelector('[data-bar-pct]').textContent  = rec.second.confidence + '%';
      setTimeout(function () {
        secondaryBar.querySelector('[data-bar-fill]').style.width = rec.second.confidence + '%';
      }, 50);
    } else {
      secondaryBar.style.display = 'none';
    }

    preSelectVariant(rec.recommended_size, STATE.size_option_name);
  }

  /* ─── copy / text overrides ────────────────────────────────────── */
  var COPY_DEFAULTS = {
    step1_title:             "What's your waist size?",
    step1_subtitle:          "Measure around where your waistband sits, snug but not squeezing.",
    waist_label:             "Waist",
    estimate_link:           "No tape measure? Estimate from height & weight",
    step2_title:             "How do you like it to feel?",
    step2_subtitle:          "Adjusts your recommendation if you're near a size boundary.",
    option_fitted_title:     "Fitted",
    option_fitted_sub:       "Firm, close to body",
    option_just_right_title: "Just right",
    option_just_right_sub:   "True to size",
    option_loose_title:      "Relaxed",
    option_loose_sub:        "More room to move",
    result_intro:            "We recommend this size for you.",
    result_note:             "Based on your measurements and fit preference."
  };

  function applyCopy(copy) {
    var t = {};
    Object.keys(COPY_DEFAULTS).forEach(function (k) {
      t[k] = (copy && copy[k]) ? copy[k] : COPY_DEFAULTS[k];
    });

    function setText(selector, value) {
      var el = rootEl.querySelector(selector);
      if (el) el.textContent = value;
    }

    setText('[data-step="1"] .biofit-fa-step__title',  t.step1_title);
    setText('[data-step="1"] .biofit-fa-step__sub',    t.step1_subtitle);
    setText('[data-step="2"] .biofit-fa-step__title',  t.step2_title);
    setText('[data-step="2"] .biofit-fa-step__sub',    t.step2_subtitle);
    setText('.biofit-fa-result__intro',                t.result_intro);
    setText('.biofit-fa-result__note',                 t.result_note);

    /* waist field label */
    setText('[data-mode-panel="waist"] .biofit-fa-field__label', t.waist_label);

    /* estimate mode switch link */
    setText('[data-switch-mode="estimate"]', t.estimate_link);

    /* fit options */
    var optionMap = { fitted: ['option_fitted_title', 'option_fitted_sub'], just_right: ['option_just_right_title', 'option_just_right_sub'], loose: ['option_loose_title', 'option_loose_sub'] };
    rootEl.querySelectorAll('.biofit-fa-option').forEach(function (opt) {
      var key = opt.getAttribute('data-fit');
      if (!optionMap[key]) return;
      var titleEl = opt.querySelector('.biofit-fa-option__title');
      var subEl   = opt.querySelector('.biofit-fa-option__sub');
      if (titleEl) titleEl.textContent = t[optionMap[key][0]];
      if (subEl)   subEl.textContent   = t[optionMap[key][1]];
    });
  }

  /* ─── open / close ──────────────────────────────────────────────── */
  function openModal(payload, copy) {
    ensureModal();
    STATE.matrix           = payload.matrix || [];
    STATE.size_option_name = payload.size_option_name || 'Size';
    applyCopy(copy || {});

    /* full reset */
    STATE.waist      = null;
    STATE.height     = null;
    STATE.weight     = null;
    STATE.fit        = null;
    STATE.input_mode = 'waist';
    rootEl.querySelectorAll('.biofit-fa-input').forEach(function (i) { i.value = ''; });
    rootEl.querySelectorAll('.biofit-fa-option').forEach(function (o) { o.classList.remove('is-active'); });
    rootEl.querySelectorAll('[data-mode-panel]').forEach(function (panel) {
      panel.style.display = panel.getAttribute('data-mode-panel') === 'waist' ? '' : 'none';
    });
    showInputError(null);
    setStep(1);

    overlayEl.classList.add('is-open');
    modalEl.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeModal() {
    if (!rootEl) return;
    modalEl.classList.add('is-closing');
    overlayEl.classList.add('is-closing');
    setTimeout(function () {
      overlayEl.classList.remove('is-open', 'is-closing');
      modalEl.classList.remove('is-open', 'is-closing');
      document.body.style.overflow = '';
    }, 300);
  }

  /* ─── trigger binding ───────────────────────────────────────────── */
  function bindTriggers() {
    var triggers = document.querySelectorAll('.biofit-fa-trigger:not([data-fa-bound])');
    triggers.forEach(function (btn) {
      btn.setAttribute('data-fa-bound', 'true');
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var raw     = btn.getAttribute('data-fit-data');
        var rawCopy = btn.getAttribute('data-copy');
        if (!raw) return;
        try {
          var parsed = JSON.parse(raw);
          var copy   = rawCopy ? JSON.parse(rawCopy) : {};
          if (!parsed.matrix || !parsed.matrix.length) return;
          openModal(parsed, copy);
        } catch (err) { /* malformed JSON — fail silently */ }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindTriggers);
  } else {
    bindTriggers();
  }
  document.addEventListener('shopify:section:load', bindTriggers);
})();
